import moment from 'moment';
import { statusToChip as integracaoStatusToChip } from '../../../Integracoes/constants';

export const INTEGRACAO_LABELS = {
  SYNCED: 'NSMP integrada',
  ERROR_SYNC: 'Erro na integração',
  NOT_SYNC: 'Não sincronizado',
  NAO_ENVIADA: 'Não enviada',
};

export const ORIGEM_LABELS = {
  DIGITACAO: 'Digitação original',
  DIGITACAO_ADMIN: 'Digitação admin',
  CORRECAO_DIGITADOR: 'Correção digitador',
  CORRECAO_ADMIN: 'Correção admin',
};

export const STATUS_APROVACAO_LABELS = {
  VALIDO: 'Válido',
  AGUARDANDO_VALIDACAO: 'Aguardando validação',
  INVALIDO: 'Inválido',
  NAO_INTEGRAR: 'Não integrar',
};

const ADMIN_ORIGENS = ['DIGITACAO_ADMIN', 'CORRECAO_ADMIN'];

export const getOrigemLabel = (origem) => ORIGEM_LABELS[origem] || origem || null;

export const getStatusAprovacaoLabel = (status) =>
  STATUS_APROVACAO_LABELS[status] || status || null;

/** Aceita epoch ms (number) ou ISO string (ex.: 2026-07-30T18:57:11.017553Z). */
export const formatDigitadoEm = (digitadoEm, format = 'DD/MM/YYYY HH:mm') => {
  if (digitadoEm == null || digitadoEm === '') return null;
  const m = typeof digitadoEm === 'number'
    ? moment(digitadoEm)
    : moment(digitadoEm);
  return m.isValid() ? m.format(format) : null;
};

export const isNaoIntegrarAutomatico = (resumoIntegracao) => {
  if (!resumoIntegracao) return false;
  if (resumoIntegracao.statusAprovacao === 'NAO_INTEGRAR') return true;
  return ADMIN_ORIGENS.includes(resumoIntegracao.origem);
};

export const getResumoIntegracao = (row) => row?.resumoIntegracao ?? null;

export const getIntegracaoStatus = (row) => {
  const status = getResumoIntegracao(row)?.status ?? row?.integrationStatus ?? null;
  return status || null;
};

export const isDigitadoSemIntegracaoSync = (row) => {
  const syncStatus = getIntegracaoStatus(row);
  if (syncStatus === 'NOT_SYNC') return true;
  const ri = getResumoIntegracao(row);
  if (ri && isNaoIntegrarAutomatico(ri)) return true;
  return false;
};

export const hasIntegracaoHistorico = (row) => (row?.totalEventos ?? 0) > 1;

export const integracaoChipFromRow = (row) => {
  const ri = getResumoIntegracao(row);
  const syncStatus = getIntegracaoStatus(row);

  if (syncStatus === 'SYNCED') {
    return { ...integracaoStatusToChip(syncStatus), label: INTEGRACAO_LABELS.SYNCED };
  }
  if (syncStatus === 'ERROR_SYNC') {
    return { ...integracaoStatusToChip(syncStatus), label: INTEGRACAO_LABELS.ERROR_SYNC };
  }
  if (syncStatus === 'NOT_SYNC') {
    return { ...integracaoStatusToChip(syncStatus), label: INTEGRACAO_LABELS.NOT_SYNC };
  }

  if (ri && isNaoIntegrarAutomatico(ri) && (row?.totalEventos ?? 0) > 0) {
    return { value: 'NAO_ENVIADA', label: INTEGRACAO_LABELS.NAO_ENVIADA };
  }

  return null;
};

export const getIntegracaoCaption = (row) => {
  const ri = getResumoIntegracao(row);
  const syncStatus = getIntegracaoStatus(row);

  if (ri && isNaoIntegrarAutomatico(ri) && (row?.totalEventos ?? 0) > 0) {
    return getOrigemLabel(ri.origem) || 'Sem envio automático';
  }
  if (syncStatus === 'ERROR_SYNC') return 'Ver detalhes';
  if (syncStatus === 'NOT_SYNC') return 'Aguardando envio';
  if (hasIntegracaoHistorico(row)) return 'Ver histórico';
  return null;
};

export const getIntegracaoDisplayChip = (row) => {
  const chip = integracaoChipFromRow(row);
  if (!chip) return null;
  const caption = getIntegracaoCaption(row);
  if (!caption) return chip;
  return { ...chip, label: `${chip.label} · ${caption}` };
};

export const getIntegracaoDetail = (row) => {
  const ri = getResumoIntegracao(row);
  const syncStatus = getIntegracaoStatus(row);
  const origemLabel = getOrigemLabel(ri?.origem);
  const totalEventos = row?.totalEventos ?? 0;

  if (syncStatus === 'SYNCED') {
    return {
      title: INTEGRACAO_LABELS.SYNCED,
      body: 'Esta digitação foi enviada ao terceiro com sucesso.',
      severity: 'success',
      origemLabel,
      statusAprovacao: ri?.statusAprovacao,
      showTimeline: totalEventos > 0,
      tooltip: 'Integrada com o terceiro — clique para detalhes.',
    };
  }
  if (syncStatus === 'ERROR_SYNC') {
    return {
      title: INTEGRACAO_LABELS.ERROR_SYNC,
      body: ri?.erro || 'Falha ao sincronizar com o terceiro.',
      severity: 'error',
      origemLabel,
      statusAprovacao: ri?.statusAprovacao,
      showTimeline: totalEventos > 0,
      tooltip: 'Erro na integração — clique para detalhes.',
      showReprocessHint: true,
    };
  }
  if (syncStatus === 'NOT_SYNC') {
    return {
      title: INTEGRACAO_LABELS.NOT_SYNC,
      body: 'Digitação válida aguardando envio ao terceiro.',
      severity: 'info',
      origemLabel,
      statusAprovacao: ri?.statusAprovacao,
      showTimeline: totalEventos > 0,
      tooltip: 'Envio pendente — clique para detalhes.',
    };
  }
  if (ri && isNaoIntegrarAutomatico(ri) && totalEventos > 0) {
    return {
      title: INTEGRACAO_LABELS.NAO_ENVIADA,
      body: 'Correções feitas por admin não são enviadas automaticamente ao terceiro. Não é um erro de integração.',
      severity: 'info',
      origemLabel,
      statusAprovacao: ri?.statusAprovacao,
      showTimeline: true,
      tooltip: 'Não enviada por regra — clique para ver o histórico.',
    };
  }
  return null;
};

export const normalizeEventoResumo = (ev) => ({
  id: ev.id,
  ordem: ev.ordem,
  digitadoEm: ev.digitadoEm,
  digitadorNome: ev.digitadorNome || ev.digitador?.nome || '—',
  origemLabel: ev.origemLabel || getOrigemLabel(ev.origem) || ev.origem || '—',
  origem: ev.origem,
  statusAprovacao: ev.statusAprovacao,
  statusAprovacaoLabel: getStatusAprovacaoLabel(ev.statusAprovacao),
  alteracoes: ev.alteracoes,
});

export const getEventosResumoFromRow = (row) => {
  if (Array.isArray(row?.eventosResumo) && row.eventosResumo.length > 0) {
    return row.eventosResumo.map(normalizeEventoResumo);
  }
  return null;
};

export default integracaoChipFromRow;
